import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import Seo from '../components/Seo';

const EFFECTIVE_DATE = 'January 1, 2026';

const TOC = [
  { id: 'acceptance',     title: '1. Acceptance of Terms' },
  { id: 'eligibility',    title: '2. Eligibility' },
  { id: 'accounts',       title: '3. Account Registration and Security' },
  { id: 'subscriptions',  title: '4. Subscriptions, Billing, and Refunds' },
  { id: 'acceptable-use', title: '5. Acceptable Use' },
  { id: 'risk-disclosure',title: '6. Crypto Trading Risk Disclosure' },
  { id: 'no-advice',      title: '7. No Financial Advice' },
  { id: 'third-party',    title: '8. Third-Party Services and Wallets' },
  { id: 'ip',             title: '9. Intellectual Property' },
  { id: 'user-content',   title: '10. User Content' },
  { id: 'termination',    title: '11. Termination' },
  { id: 'disclaimers',    title: '12. Disclaimers' },
  { id: 'liability',      title: '13. Limitation of Liability' },
  { id: 'indemnity',      title: '14. Indemnification' },
  { id: 'governing-law',  title: '15. Governing Law and Dispute Resolution' },
  { id: 'changes',        title: '16. Changes to These Terms' },
  { id: 'misc',           title: '17. Miscellaneous' },
  { id: 'contact',        title: '18. Contact Us' },
];

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 mb-10">
      <h2 className="text-xl font-semibold text-white mb-3">{title}</h2>
      <div className="space-y-3 text-[15px] leading-relaxed text-gray-300">
        {children}
      </div>
    </section>
  );
}

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-dark-900 text-gray-200">
      <Seo
        title="Terms of Service"
        description="The legal terms governing use of haythix — subscription, content, copy trading, on-chain risk, dispute resolution. Effective January 1, 2026."
        path="/terms"
      />
      <PublicNav />

      {/* Body */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-widest text-accent-primary/80 mb-2">Terms of Service</p>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Terms of Service</h1>
          <p className="text-sm text-gray-400">
            Effective date: <span className="text-gray-200">{EFFECTIVE_DATE}</span>
          </p>
        </div>

        {/* Risk banner — make it impossible to miss */}
        <div className="card border-amber-500/30 bg-amber-500/5 mb-12">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="space-y-2 text-sm text-amber-100/90">
              <p className="font-semibold text-amber-300">Cryptocurrency trading carries substantial risk.</p>
              <p>
                You can lose some or all of the capital you put at risk. haythix provides software
                tools and information; it does not provide financial, legal, or tax advice and does
                not guarantee any outcome. Before you place a trade, read Section 6 (Risk
                Disclosure) and Section 7 (No Financial Advice) below in full.
              </p>
            </div>
          </div>
        </div>

        {/* Table of contents */}
        <nav className="card border-dark-600 mb-12">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">Contents</p>
          <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {TOC.map(({ id, title }) => (
              <li key={id}>
                <a href={`#${id}`} className="text-gray-300 hover:text-accent-primary transition-colors">
                  {title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Intro */}
        <div className="space-y-3 text-[15px] leading-relaxed text-gray-300 mb-12">
          <p>
            These Terms of Service (&ldquo;Terms&rdquo;) form a binding legal agreement between you and
            <strong> Borak Solutions LLC</strong>, a Wisconsin limited liability company
            (&ldquo;haythix,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;), governing your access to and use of the
            haythix website at <em>haythix.com</em>, our application programming interfaces, and
            any related software, mobile applications, or services (collectively, the &ldquo;Service&rdquo;).
          </p>
          <p>
            By creating an account, clicking &ldquo;I agree,&rdquo; or otherwise using the Service, you
            confirm that you have read, understood, and agree to be bound by these Terms and by
            our <Link to="/privacy" className="text-accent-primary hover:underline">Privacy Policy</Link>.
            If you do not agree, do not use the Service.
          </p>
        </div>

        <Section id="acceptance" title="1. Acceptance of Terms">
          <p>
            These Terms, together with the Privacy Policy and any additional terms we present to
            you for specific features (such as wallet connections, exchange integrations, or the
            strategy marketplace), constitute the entire agreement between you and us regarding
            the Service. If there is a conflict between these Terms and a feature-specific term,
            the feature-specific term controls for that feature.
          </p>
        </Section>

        <Section id="eligibility" title="2. Eligibility">
          <p>
            You must be at least eighteen (18) years old and able to form a legally binding
            contract under the laws of your jurisdiction to use the Service. You may not use the
            Service if you are located in, or are a resident of, any jurisdiction in which use of
            the Service would be unlawful, or if you are subject to economic sanctions imposed by
            the United States, the United Nations, the European Union, or the United Kingdom. You
            are solely responsible for determining whether your use of the Service is lawful in
            your jurisdiction.
          </p>
        </Section>

        <Section id="accounts" title="3. Account Registration and Security">
          <p>
            To use most features of the Service you must create an account. You agree to provide
            accurate, current, and complete information, and to keep that information up to date.
            You are responsible for maintaining the confidentiality of your password, for any
            activity that occurs under your account, and for promptly notifying us of any
            suspected unauthorized access. We strongly recommend enabling two-factor
            authentication. We are not liable for losses caused by unauthorized account access
            that you could have prevented by reasonable security measures.
          </p>
        </Section>

        <Section id="subscriptions" title="4. Subscriptions, Billing, and Refunds">
          <p>
            The Service is offered on a subscription basis with a free trial and several paid
            tiers as described on our pricing page. Subscription fees are billed in advance on a
            recurring basis (monthly or annually) by our payment processor, Stripe. Prices are
            shown in U.S. dollars and exclude any applicable taxes, which you are responsible for.
          </p>
          <p>
            Your subscription will renew automatically at the end of each billing period at the
            then-current rate unless you cancel before the renewal date. You can cancel at any
            time from your account settings; cancellation takes effect at the end of the current
            billing period.
          </p>
          <p>
            <strong>Refund policy.</strong> All charges are final and non-refundable except where
            required by law. We may, at our sole discretion, issue a prorated refund for an annual
            plan if you cancel within fourteen (14) days of the initial charge and have not
            materially used paid features during that period.
          </p>
          <p>
            <strong>Trials.</strong> If we offer a free trial, you may use paid features for the
            stated trial period at no cost. Unless you cancel before the trial ends, the
            subscription will convert to a paid plan and you will be charged the applicable
            recurring fee.
          </p>
          <p>
            <strong>Promotional pricing.</strong> Promotional discounts (such as the Founders
            program) are non-transferable and may be discontinued at any time for new sign-ups,
            but will be honored for existing subscribers as long as the underlying subscription
            remains active.
          </p>
        </Section>

        <Section id="acceptable-use" title="5. Acceptable Use">
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Use the Service to violate any applicable law, regulation, or third-party right;</li>
            <li>Attempt to gain unauthorized access to any part of the Service, other accounts, or our infrastructure;</li>
            <li>Reverse-engineer, decompile, or attempt to extract source code from the Service except to the extent expressly permitted by law;</li>
            <li>
              Engage in market manipulation in any form, including but not limited to:
              <span className="block ml-4 mt-1">
                – <strong>Wash trading</strong> (buying and selling the same asset between wallets or accounts that you control, directly or indirectly, to create artificial volume or price signals);
              </span>
              <span className="block ml-4">
                – <strong>Coordinated multi-account or multi-wallet trading</strong> intended to influence price or volume of any asset;
              </span>
              <span className="block ml-4">
                – <strong>Spoofing, layering, painting the tape</strong>, or placing orders without intent to execute;
              </span>
              <span className="block ml-4">
                – Any activity prohibited by the rules of any exchange whose API key you connect to haythix. Violators forfeit Service access without refund and may be reported to the affected exchange and applicable regulators;
              </span>
            </li>
            <li>Send spam, malware, or other harmful content through any community or messaging feature;</li>
            <li>Resell, sublicense, or commercially exploit the Service except as expressly permitted in these Terms;</li>
            <li>Use the Service to operate unregistered money-transmission, custody, or fund-management businesses; or</li>
            <li>Circumvent any access restriction, rate limit, or feature gate.</li>
          </ul>
          <p>
            We may suspend or terminate access if we believe in good faith that you have violated
            these rules or that your use of the Service poses a risk to us or other users.
          </p>
        </Section>

        <Section id="risk-disclosure" title="6. Crypto Trading Risk Disclosure">
          <p>
            <strong>You should not trade with money you cannot afford to lose.</strong> Trading
            cryptocurrencies and digital assets involves a high level of risk and is not suitable
            for every investor. Among other risks:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Cryptocurrency prices are highly volatile and can move sharply against you in seconds. Past performance does not predict future results.</li>
            <li>Automated strategies, signals, scanners, and forecasts presented in the Service are tools, not predictions. They may be wrong, may produce losses, and may behave unexpectedly under unusual market conditions.</li>
            <li>On-chain transactions and exchange orders are executed externally to haythix. Network congestion, slippage, gas fees, MEV, exchange outages, partial fills, and order rejections can all affect outcomes.</li>
            <li>Smart contracts can contain bugs or be exploited, resulting in partial or total loss of funds you deploy on-chain.</li>
            <li>Centralized exchanges may be hacked, suspend withdrawals, freeze accounts, or become insolvent. We have no control over an exchange&rsquo;s actions.</li>
            <li>Self-custodial wallets put the burden of key management on you. If you lose your seed phrase or expose your private keys, your funds are gone and we cannot recover them.</li>
            <li>Tax treatment of cryptocurrency activity varies by jurisdiction and is your responsibility to determine.</li>
          </ul>
          <p>
            Forecast accuracy figures, backtest results, simulated performance, and historical
            comparisons shown anywhere in the Service are illustrative and based on assumptions
            that may not hold in live markets. Live results may differ materially.
          </p>
        </Section>

        <Section id="no-advice" title="7. No Financial Advice">
          <p>
            The Service provides information, software tools, and analytics. Nothing in the
            Service is, or should be construed as, investment advice, financial advice, legal
            advice, accounting advice, or tax advice. We are not a registered investment adviser,
            broker-dealer, futures commission merchant, money-services business, or licensed
            financial professional, and we do not act in any fiduciary capacity to you. You are
            solely responsible for your own trading decisions and for consulting qualified
            professionals where appropriate.
          </p>
        </Section>

        <Section id="third-party" title="8. Third-Party Services and Wallets">
          <p>
            The Service integrates with third-party services such as cryptocurrency exchanges,
            blockchain networks, wallet providers, market-data providers, and payment processors.
            Your use of those services is governed by their own terms and privacy policies. We
            are not responsible for the actions, omissions, fees, outages, or security practices
            of any third party. When you connect a wallet, you remain in sole control of your
            keys and your transactions; we never take custody of your funds.
          </p>
        </Section>

        <Section id="ip" title="9. Intellectual Property">
          <p>
            The Service, including all software, designs, text, graphics, logos, trademarks, and
            documentation, is owned by Borak Solutions LLC or its licensors and is protected by
            intellectual-property laws. Subject to your compliance with these Terms, we grant you
            a limited, non-exclusive, non-transferable, revocable license to access and use the
            Service for your personal or internal business use during the term of your
            subscription. We reserve all rights not expressly granted.
          </p>
          <p>
            &ldquo;haythix&rdquo; and the haythix logo are trademarks of Borak Solutions LLC. You may not use
            our trademarks without our prior written permission, except in fair, descriptive
            references that do not imply endorsement.
          </p>
        </Section>

        <Section id="user-content" title="10. User Content">
          <p>
            If you post content to the Service (for example, a public profile, a marketplace
            strategy, a community message, or a tokenomics report), you retain ownership of that
            content. You grant us a worldwide, non-exclusive, royalty-free license to host,
            store, display, and distribute that content as needed to operate the Service and to
            promote the Service in a reasonable manner. You represent that you have the right to
            grant this license and that your content does not violate any third-party right.
          </p>
          <p>
            We may, but are not required to, review, remove, or edit user content that we believe
            violates these Terms or applicable law.
          </p>
        </Section>

        <Section id="termination" title="11. Termination">
          <p>
            You may close your account at any time. We may suspend or terminate your access at any
            time for any reason, including for violation of these Terms or for non-payment. On
            termination, your right to use the Service ends immediately. Sections that by their
            nature should survive termination (including Sections 6, 7, 9, 12, 13, 14, and 15)
            will survive.
          </p>
        </Section>

        <Section id="disclaimers" title="12. Disclaimers">
          <p className="uppercase text-xs tracking-wide text-gray-400">
            The following section is presented in capital letters because applicable law requires it to be conspicuous.
          </p>
          <p className="text-gray-200">
            THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE,&rdquo; WITHOUT WARRANTY OF ANY KIND,
            EXPRESS OR IMPLIED. TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES,
            INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
            NON-INFRINGEMENT, AND ANY WARRANTY ARISING OUT OF COURSE OF DEALING OR USAGE OF TRADE.
            WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE
            OF VIRUSES OR OTHER HARMFUL COMPONENTS, OR THAT ANY DEFECT WILL BE CORRECTED. WE DO
            NOT WARRANT THE ACCURACY, COMPLETENESS, RELIABILITY, OR TIMELINESS OF ANY MARKET
            DATA, SIGNAL, FORECAST, BACKTEST, OR ANALYSIS PROVIDED THROUGH THE SERVICE.
          </p>
        </Section>

        <Section id="liability" title="13. Limitation of Liability">
          <p className="text-gray-200">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT WILL BORAK SOLUTIONS LLC, ITS
            AFFILIATES, OFFICERS, EMPLOYEES, AGENTS, OR LICENSORS BE LIABLE FOR ANY INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF
            PROFITS, REVENUE, DATA, GOODWILL, OR TRADING LOSSES, WHETHER ARISING IN CONTRACT,
            TORT, STRICT LIABILITY, OR ANY OTHER LEGAL THEORY, EVEN IF WE HAVE BEEN ADVISED OF
            THE POSSIBILITY OF SUCH DAMAGES.
          </p>
          <p className="text-gray-200">
            OUR TOTAL CUMULATIVE LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THE
            SERVICE OR THESE TERMS WILL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID US IN
            THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM, OR (B) ONE
            HUNDRED U.S. DOLLARS (US$100). THESE LIMITATIONS APPLY EVEN IF A REMEDY FAILS OF ITS
            ESSENTIAL PURPOSE. SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN DAMAGES,
            SO PORTIONS OF THIS SECTION MAY NOT APPLY TO YOU.
          </p>
        </Section>

        <Section id="indemnity" title="14. Indemnification">
          <p>
            You agree to defend, indemnify, and hold harmless Borak Solutions LLC and its
            affiliates, officers, employees, and agents from and against any claims, damages,
            liabilities, costs, and expenses (including reasonable attorneys&rsquo; fees) arising out
            of or relating to (a) your use of the Service, (b) your violation of these Terms, (c)
            your violation of any third-party right, including any intellectual-property or
            privacy right, or (d) any trading decision you make or fail to make.
          </p>
        </Section>

        <Section id="governing-law" title="15. Governing Law and Dispute Resolution">
          <p>
            These Terms are governed by the laws of the State of Wisconsin, United States of
            America, without regard to its conflict-of-laws principles. The United Nations
            Convention on Contracts for the International Sale of Goods does not apply.
          </p>
          <p>
            <strong>Informal resolution first.</strong> Before filing any claim, you agree to
            first contact us at <a href="mailto:info@haythix.com" className="text-accent-primary hover:underline">info@haythix.com</a> and
            attempt to resolve the dispute informally for at least sixty (60) days.
          </p>
          <p>
            <strong>Venue.</strong> If informal resolution fails, you and we agree that any
            lawsuit will be brought exclusively in the state or federal courts located in
            Milwaukee County, Wisconsin, and you and we consent to the personal jurisdiction of
            those courts.
          </p>
          <p>
            <strong>Class-action waiver.</strong> To the extent permitted by law, you and we agree
            that disputes will be resolved on an individual basis only, and not as part of a
            class, consolidated, or representative action.
          </p>
        </Section>

        <Section id="changes" title="16. Changes to These Terms">
          <p>
            We may update these Terms from time to time. When we do, we will post the updated
            version on this page and update the effective date. If the changes are material, we
            will notify you by email or by a prominent notice in the Service before the changes
            take effect. Your continued use of the Service after the effective date of the
            updated Terms constitutes your acceptance of the changes. If you do not agree to the
            updated Terms, you must stop using the Service.
          </p>
        </Section>

        <Section id="misc" title="17. Miscellaneous">
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Severability.</strong> If any provision of these Terms is held unenforceable, the remaining provisions will remain in full force and effect.</li>
            <li><strong>No waiver.</strong> Our failure to enforce a provision is not a waiver of our right to enforce it later.</li>
            <li><strong>Assignment.</strong> You may not assign these Terms without our prior written consent. We may assign these Terms to an affiliate or in connection with a merger, acquisition, or sale of assets.</li>
            <li><strong>No agency.</strong> No partnership, joint venture, employment, or agency relationship is created by these Terms.</li>
            <li><strong>Notices.</strong> We may give notices to you by email or by posting them in the Service. Notices to us must be sent to the email below.</li>
            <li><strong>Force majeure.</strong> We are not liable for delays or failures caused by events beyond our reasonable control, including network outages, exchange outages, blockchain congestion, regulatory action, natural disasters, or labor disputes.</li>
          </ul>
        </Section>

        <Section id="contact" title="18. Contact Us">
          <p>For questions about these Terms, contact us at:</p>
          <p>
            <strong>Borak Solutions LLC</strong><br />
            State of Wisconsin, United States of America<br />
            Email: <a href="mailto:info@haythix.com" className="text-accent-primary hover:underline">info@haythix.com</a>
          </p>
        </Section>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-dark-700 text-center text-xs text-gray-500">
          <p>
            <Link to="/terms" className="hover:text-accent-primary transition-colors">Terms of Service</Link>
            <span className="mx-2">&middot;</span>
            <Link to="/privacy" className="hover:text-accent-primary transition-colors">Privacy Policy</Link>
          </p>
          <p className="mt-3">&copy; {new Date().getFullYear()} Borak Solutions LLC. All rights reserved.</p>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
